import { Injectable } from '@angular/core';
import {
  AvailableReportsViewModel,
  DownloadableReportType,
  DownloadableReportParameter,
  DownloadableReportParameterValues,
  GenerateDownloadableReportParams,
} from '@app/reporting/downloadable/models/downloadable-reports.model';
import {
  AvailableReport,
  AvailableReportsResponse,
  DownloadableReport,
  GenerateDownloadableReportRequest,
} from '@app/reporting/models/reporting-api.model';
import { NgxHttpClient } from '@app/shared/ngx-http-client';
import { DateRangeService } from '@app/shared/services/date-range.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { camelCaseKeys } from '@app/shared/utils/property';
import { TranslateService } from '@ngx-translate/core';
import { catchAndSurfaceError } from '@app/shared/utils/dg-error-helpers';
import { isPlainObject } from 'lodash-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DownloadableReportsApiService {
  /**
   * Parses report parameters from a given report method
   * Parameters are stored for a Report as: `{"OrganizationId":<OrganizationId>,"InputIdentifier":<InputIdentifier>}`
   */
  public static parseReportParameters(
    report: AvailableReport
  ): DownloadableReportParameter[] {
    if (!report.parameters) {
      return;
    }

    // HACK: convert the format stored in the DB to valid JSON
    const parameters = report.parameters.replace(
      /"?<(\w+)>"?/g,
      (match, paramType: string) => {
        return `"${paramType}"`;
      }
    );
    const supportedParameterTypes = ['InputIdentifier', 'InputIdentifierList'];
    try {
      const kv = camelCaseKeys(JSON.parse(parameters));
      return Object.keys(kv)
        .map((key) => ({ key, type: kv[key] }))
        .filter((param) => supportedParameterTypes.indexOf(param.type) !== -1);
    } catch (error) {
      console.warn('Failed to parse report parameters', {
        report,
        parameters,
        error,
      });
    }
  }

  // Extract and format report parameters based on the selected report type
  public static formatReportParameters(
    report: DownloadableReportType,
    values?: any
  ): DownloadableReportParameterValues {
    const expectedParameters = report.reportParameters;
    if (
      !expectedParameters?.length ||
      'undefined' === typeof values ||
      !isPlainObject(values)
    ) {
      return;
    }

    const reportParams: DownloadableReportParameterValues = {};
    for (const param of expectedParameters) {
      const { key, type } = param;
      if (!values[key]) {
        continue;
      }

      switch (type) {
        case 'InputIdentifier':
          reportParams[key] = {
            inputId: values[key].inputId,
            inputType: values[key].inputType,
          };
          break;
        case 'InputIdentifierList':
          reportParams[key] = values[key].map((input) => ({
            inputId: input.inputId,
            inputType: input.inputType,
          }));
          break;
      }
    }

    return reportParams;
  }

  public readonly i18n = this.translate.instant([
    // Downloadable Report Types
    'dgDownloadableReports_ReportContentTrendsAdded',
    'dgDownloadableReports_ReportContentTrendsViewed',
    'dgDownloadableReports_ReportPopularTopics',
    'dgDownloadableReports_ReportTopProviders',
    'dgDownloadableReports_ReportTopSearches',
    'dgDownloadableReports_ReportPathwayDetailsDownload',
    'dgDownloadableReports_ReportSkillReport',
    'dgDownloadableReports_ReportTop1000SkillsRated',
    'dgDownloadableReports_ReportContentCompletionsDownload',
    'dgDownloadableReports_ReportSkillAnalyticsTop1000SkillsRated',
  ]);

  constructor(
    private http: NgxHttpClient,
    private tracker: TrackerService,
    private translate: TranslateService,
    private dateRangeService: DateRangeService
  ) {}

  /**
   * Get the list of available reports for a user
   */
  public getDownloadableReports(
    location: string,
    pathwayId?: number
  ): Observable<AvailableReportsViewModel> {
    this.tracker.trackEventData({
      action: 'Downloadable Reports Clicked',
      properties: {
        location,
      },
    });
    return this.http
      .get<AvailableReportsResponse>('/reporting/GetDownloadableReports', {
        params: {
          pathwayId,
        },
      })
      .pipe(
        map((data) => {
          const reports: DownloadableReportType[] = data.reports?.map(
            (report) => ({
              ...report,
              reportName: this.getReportLabel(report),
              reportParameters: DownloadableReportsApiService.parseReportParameters(
                report
              ),
            })
          );

          return {
            ...data,
            reports,
          };
        })
      );
  }

  /**
   * Get the list of previously generated reports for the user
   */
  public getDownloadableReportsForUser(): Observable<DownloadableReport[]> {
    return this.http
      .get<DownloadableReport[]>('/reporting/GetDownloadableReportsForUser')
      .pipe(
        map((reports) =>
          reports?.map((report) => this.normalizeDownloadableReport(report))
        )
      );
  }

  /**
   * Get the list of previously generated reports for the user, for a specific pathway
   */
  public getDownloadablePathwayReportsForUser(
    pathwayId: number,
    pathwayReportId: number
  ): Observable<DownloadableReport[]> {
    return this.http
      .get<DownloadableReport[]>('/reporting/GetDownloadableReportsForUser', {
        params: {
          pathwayId,
          reportId: pathwayReportId,
        },
      })
      .pipe(
        map((reports) =>
          reports?.map((report) => this.normalizeDownloadableReport(report))
        )
      );
  }

  /**
   * Generate a new Downloadable Report, with the given parameters
   */
  public generateDownloadableReport(
    reportName: string,
    params: GenerateDownloadableReportParams
  ): Observable<DownloadableReport> {
    const { location, selection, ...payload } = params;
    this.tracker.trackEventData({
      action: 'Report Generated',
      properties: {
        ...params,
        reportName,
        location,
        selection,
      },
    });

    return this.http
      .post<DownloadableReport>(
        '/reporting/GenerateDownloadableReport',
        payload as GenerateDownloadableReportRequest
      )
      .pipe(catchAndSurfaceError(this.translate.instant('Core_ActionError')));
  }

  /**
   * Normalize the DownloadableReport model received from the API
   */
  private normalizeDownloadableReport(
    report: DownloadableReport
  ): DownloadableReport {
    report.reportName = this.getReportLabel(report);

    // parse the report parameters (when present)
    if ('string' !== typeof report.reportParameters) {
      return report;
    }

    try {
      const parsed = JSON.parse(report.reportParameters);
      const params = camelCaseKeys(parsed);
      report.reportParameters = params;
    } catch (e) {
      // ignore parse errors
    }

    return report;
  }

  private getReportLabel(report: {
    reportType?: string;
    reportKey?: string;
  }): string {
    const key = report.reportType
      ? report.reportType.replace(/\W/g, '')
      : report.reportKey
      ? report.reportKey
      : undefined;
    const label = this.i18n['dgDownloadableReports_Report' + key];
    if (label) {
      return label;
    }
    console.warn("Couldn't find a report label for", report);
  }
}
